import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { useForm, type FieldPath } from "react-hook-form";
import { trackJourneyEvent } from "@/utils/analytics";
import {
  BOOKING_FOR_OPTIONS,
  INTEREST_OPTIONS,
  LEARNER_STATUS_OPTIONS,
  PATHWAY_FORM_DESCRIPTION,
  PATHWAY_FORM_HEADING,
  PATHWAY_FORM_SUBMIT_LABEL,
  PATHWAY_SELECTOR_CALLBACK_SOURCE,
  PATHWAY_SELECTOR_PAGE_PATH,
  PAYMENT_PLAN_OPTIONS,
  START_TIMEFRAME_OPTIONS,
  TRAINED_WITH_CC_OPTIONS,
  TRAINING_ROUTE_OPTIONS,
  buildPathwayLeadTags,
} from "@/utils/pathway-selector";
import {
  currentSourcePage,
  getRetryKey,
  PATHWAYS_SCHEMA_VERSION,
  PRIVACY_NOTICE_VERSION,
  safeSummaryValue,
  splitContactName,
  validateContactName,
  validateContactPhone,
  pathwayFormPrefill,
} from "@/utils/enquiry-forms";
import { PATHWAY_COURSE_KEYS, type PathwayCourseKey } from "@/utils/pathway-rules";

type PathwayContext = {
  answers?: Record<string, unknown>;
  result?: {
    rulesVersion?: string;
    outcome?: string;
    optionIds?: string[];
    reasonCodes?: string[];
    status?: string;
    nextAction?: string;
    [key: string]: unknown;
  };
};

type PathwaySelectorRequestProps = {
  formId?: string;
  defaultLearnerStatus?: string;
  defaultInterest?: string;
  defaultTrainingRoute?: string;
  selectedPathwayOption?: string;
  heading?: string;
  description?: string;
  submitButtonLabel?: string;
  sourcePage?: string;
  placement?: string;
  courseHint?: string;
  entryPersona?: string;
  initialAnswers?: Record<string, unknown>;
  pathwayContext?: PathwayContext;
};

type FormValues = {
  full_name: string;
  email: string;
  phone_number: string;
  preferred_contact_method: string;
  learner_status: string;
  area_of_interest: string;
  current_qualifications: string;
  training_route: string;
  start_timeframe: string;
  practical_attendance: string;
  payment_plan_interest: string;
  trained_with_cc_before: string;
  booking_for: string;
  marketing_email_opt_in: boolean;
};

type RequestResult = { reference?: string; leadID?: string };

const inputClass = (hasError: boolean) =>
  `block w-full rounded-2xl border px-4 py-3 text-stone-900 shadow-sm transition focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-200 ${
    hasError ? "border-red-500" : "border-stone-300"
  }`;

function FieldError({ message, id }: { message?: string; id?: string }) {
  return message ? <p id={id} className="mt-2 text-sm text-red-600">{message}</p> : null;
}

function answerString(answers: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = answers[key];
    if (typeof value === "string") return value;
  }
  return "";
}

export default function PathwaySelectorRequest({
  formId = "pathway-form",
  defaultLearnerStatus,
  defaultInterest,
  defaultTrainingRoute,
  selectedPathwayOption,
  heading = PATHWAY_FORM_HEADING,
  description = PATHWAY_FORM_DESCRIPTION,
  submitButtonLabel = PATHWAY_FORM_SUBMIT_LABEL,
  sourcePage,
  placement = "pathway_selector_inline",
  courseHint,
  entryPersona,
  initialAnswers,
  pathwayContext,
}: PathwaySelectorRequestProps) {
  const router = useRouter();
  const [hydrated, setHydrated] = useState(false);
  const [state, setState] = useState<{
    complete: boolean;
    loading: boolean;
    error: string | null;
    started: boolean;
    reference: string | null;
    submitted: Partial<FormValues> | null;
  }>({ complete: false, loading: false, error: null, started: false, reference: null, submitted: null });
  const retryRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const startedRef = useRef(false);

  const answers = useMemo(
    () => ({ ...(pathwayContext?.answers || {}), ...(initialAnswers || {}) }),
    [pathwayContext?.answers, initialAnswers],
  );
  const initialPrefill = pathwayFormPrefill(answers);
  const entryPersonaKey = entryPersona || answerString(answers, "entryPersona");

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onTouched",
    defaultValues: {
      learner_status: defaultLearnerStatus || initialPrefill.learnerStatus,
      area_of_interest: defaultInterest || initialPrefill.areaOfInterest,
      training_route: defaultTrainingRoute || initialPrefill.trainingRoute,
      preferred_contact_method: "email",
      payment_plan_interest: "",
      start_timeframe: initialPrefill.startTimeframe,
      practical_attendance: initialPrefill.practicalAttendance,
      trained_with_cc_before: "",
      booking_for: initialPrefill.bookingFor,
      marketing_email_opt_in: false,
    },
  });
  const preferredContact = watch("preferred_contact_method");
  const currentBooking = watch("booking_for");

  useEffect(() => {
    setHydrated(true);
    if (defaultLearnerStatus) setValue("learner_status", defaultLearnerStatus);
    if (defaultInterest) setValue("area_of_interest", defaultInterest);
    if (defaultTrainingRoute) setValue("training_route", defaultTrainingRoute);
    if (defaultLearnerStatus === "salon_clinic_owner") setValue("booking_for", "team");
  }, [defaultLearnerStatus, defaultInterest, defaultTrainingRoute, setValue]);

  const markStarted = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setState((current) => ({ ...current, started: true }));
    trackJourneyEvent("pathway_enquiry_form_started", {
      form_id: formId,
      placement,
      page_key: sourcePage?.split("/").filter(Boolean).pop() || "pathway_selector",
      persona: entryPersonaKey,
      course_key: courseHint,
      form_type: "individual",
      audience: "individual",
    });
  };

  const onSubmit = async (data: FormValues) => {
    if (data.booking_for === "team" || data.booking_for === "both") {
      void router.push("/business-training/#planning-form");
      return;
    }

    const { first_name, last_name } = splitContactName(data.full_name);
    const tags = buildPathwayLeadTags({
      learner_status: data.learner_status,
      area_of_interest: data.area_of_interest,
      training_route: data.training_route,
      booking_for: data.booking_for,
      trained_with_cc_before: data.trained_with_cc_before,
      selected_pathway: selectedPathwayOption,
    });
    const inputAnswers = { ...answers };
    const trainingGoal = data.area_of_interest === "beauty_therapy" ? "beauty_facials" : data.area_of_interest === "skin_treatments" ? "advanced_skin" : data.area_of_interest === "injectables_level_7" ? "injectables" : data.area_of_interest === "rpl" ? answerString(answers, "treatmentGoal") || "unsure" : data.area_of_interest === "online_cpd" ? "unsure" : data.area_of_interest;
    const trainingIntent = data.training_route === "regulated" ? "regulated_qualification" : data.training_route === "rpl" ? "prior_learning_review" : data.training_route === "cpd" || data.training_route === "online" || data.training_route === "practical" ? "specific_skill" : "unsure";
    const primaryBackground = data.learner_status === "complete_beginner" ? "beginner" : data.learner_status === "beauty_therapist" ? "beauty_therapist" : data.learner_status === "cpd_trained" || data.learner_status === "regulated_qualified" ? "aesthetics_practitioner" : data.learner_status === "medic_healthcare" ? "medical_professional" : data.learner_status === "unsure" ? "unsure" : "other";
    const startTimeframe = data.start_timeframe === "asap" || data.start_timeframe === "this_month" ? "soon" : data.start_timeframe === "one_to_three_months" ? "within_three_months" : data.start_timeframe === "later" ? "three_to_six_months" : data.start_timeframe === "unsure" ? "unknown" : "exploring";
    const resolvedEntryPersona = entryPersona || answerString(answers, "entryPersona") || "unknown";
    const courseKeyHint = courseHint || answerString(answers, "courseHint") || null;
    const normalizedContext = {
      entry_persona: resolvedEntryPersona,
      answers: {
        ...inputAnswers,
        entryPersona: resolvedEntryPersona,
        bookingFor: data.booking_for === "myself" ? "self" : "unknown",
        primaryBackground,
        treatmentGoal: trainingGoal,
        trainingIntent,
        startTimeframe: data.start_timeframe ? startTimeframe : "unknown",
        canAttendPractical: data.practical_attendance === "yes" || data.practical_attendance === "no" ? data.practical_attendance : "unknown",
        ...(courseKeyHint ? { courseHint: courseKeyHint } : {}),
      },
      form_answers: {
        primary_background: primaryBackground,
        treatment_goal: trainingGoal,
        training_intent: trainingIntent,
        ...(data.start_timeframe ? { start_timeframe: startTimeframe } : { start_timeframe: "unknown" }),
        can_attend_practical: data.practical_attendance || "unknown",
        trained_with_cc_before: data.trained_with_cc_before || "unknown",
      },
      result: pathwayContext?.result || null,
      course_hint: courseKeyHint,
      rules_version: pathwayContext?.result?.rulesVersion || null,
      qualification_summary: data.current_qualifications.trim(),
    };
    const body = {
      type: "pathway-selector",
      first_name,
      last_name,
      email: data.email.trim().toLowerCase(),
      phone_number: preferredContact === "email" ? "" : data.phone_number.trim(),
      message: data.current_qualifications.trim(),
      preferences: {
        schema_version: PATHWAYS_SCHEMA_VERSION,
        campaign_source: PATHWAY_SELECTOR_CALLBACK_SOURCE,
        source_page: sourcePage || currentSourcePage(PATHWAY_SELECTOR_PAGE_PATH),
        form_id: formId,
        placement,
        preferred_contact_method: data.preferred_contact_method,
        learner_status: data.learner_status,
        area_of_interest: data.area_of_interest,
        training_route: data.training_route,
        ...(data.start_timeframe ? { start_timeframe: data.start_timeframe } : {}),
        ...(data.practical_attendance ? { practical_attendance: data.practical_attendance } : {}),
        ...(data.payment_plan_interest ? { payment_plan_interest: data.payment_plan_interest } : {}),
        current_qualifications: data.current_qualifications.trim(),
        ...(data.trained_with_cc_before ? { trained_with_cc_before: data.trained_with_cc_before } : {}),
        booking_for: data.booking_for === "myself" ? "self" : "unknown",
        selected_pathway_option: selectedPathwayOption || null,
        tags,
        lead_type: "individual_pathway_enquiry",
        pathway_context: normalizedContext,
      },
      permissions: {
        privacy_notice_version: PRIVACY_NOTICE_VERSION,
        marketing_email_opt_in: data.marketing_email_opt_in === true,
      },
    };

    const retry = getRetryKey(retryRef.current, body);
    retryRef.current = retry;

    try {
      setState((current) => ({ ...current, loading: true, error: null, started: true }));
      const response = await fetch("/api/callback/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, idempotency_key: retry.key }),
      });
      const result = await response.json();
      if (response.status === 400 && result?.fields && typeof result.fields === "object") {
        const mapping: Record<string, FieldPath<FormValues>> = {
          first_name: "full_name",
          last_name: "full_name",
          email: "email",
          phone_number: "phone_number",
          "preferences.learner_status": "learner_status",
          "preferences.area_of_interest": "area_of_interest",
          "preferences.training_route": "training_route",
          "preferences.start_timeframe": "start_timeframe",
          "preferences.payment_plan_interest": "payment_plan_interest",
          "preferences.trained_with_cc_before": "trained_with_cc_before",
          "preferences.pathway_context.answers": "learner_status",
        };
        for (const field of Object.keys(result.fields)) {
          const formField = mapping[field];
          if (formField) setError(formField, { type: "server", message: "Please check this answer" });
        }
        setState((current) => ({ ...current, loading: false, error: "Please review the highlighted fields and submit again." }));
        trackJourneyEvent("enquiry_submit_failed", { form_id: formId, form_type: "individual", audience: "individual", page_key: sourcePage?.split("/").filter(Boolean).pop() || "pathway_selector", placement, failure_code: "validation" });
        return;
      }
      if (!response.ok || !result?.success) throw new Error("Unable to submit enquiry");

      setState({
        complete: true,
        loading: false,
        error: null,
        started: true,
        reference: typeof result.reference === "string" ? result.reference : null,
        submitted: data,
      });

      if (result.created === true) {
        trackJourneyEvent("generate_lead", {
          form_id: formId,
          form_type: "individual",
          audience: "individual",
          page_key: sourcePage?.split("/").filter(Boolean).pop() || "pathway_selector",
          placement,
          persona: entryPersona,
          course_key: courseHint,
          rules_version: pathwayContext?.result?.rulesVersion,
          route_key: pathwayContext?.result?.optionIds?.[0],
        });
      }
    } catch {
      setState((current) => ({
        ...current,
        loading: false,
        error: "We could not confirm your enquiry was received. Your answers are still here; please retry or call admissions.",
      }));
      trackJourneyEvent("enquiry_submit_failed", {
        form_id: formId,
        form_type: "individual",
        audience: "individual",
        page_key: sourcePage?.split("/").filter(Boolean).pop() || "pathway_selector",
        placement,
        failure_code: "submission_unconfirmed",
      });
    }
  };

  if (state.complete && state.submitted) {
    return (
      <section id={formId} className="scroll-mt-24 rounded-[2rem] border border-emerald-200 bg-emerald-50 p-6 sm:p-8" aria-live="polite" data-private data-clarity-mask data-heap-redact-text data-sentry-block>
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">Enquiry received</p>
        <h2 className="mt-3 text-2xl font-bold text-stone-900">Your training enquiry has been received.</h2>
        <p className="mt-3 text-stone-700">Admissions will review the information you supplied and contact you using your selected method. Your suggested route remains subject to entry checks.</p>
        {state.reference && <p className="mt-3 text-sm text-stone-600">Reference: <span className="font-semibold">{state.reference}</span></p>}
        <dl className="mt-5 grid gap-2 text-sm text-stone-700 sm:grid-cols-2">
          <div><dt className="font-semibold">Background</dt><dd>{safeSummaryValue(state.submitted.learner_status)}</dd></div>
          <div><dt className="font-semibold">Goal</dt><dd>{safeSummaryValue(state.submitted.area_of_interest)}</dd></div>
          <div><dt className="font-semibold">Preferred contact</dt><dd>{safeSummaryValue(state.submitted.preferred_contact_method)}</dd></div>
          <div><dt className="font-semibold">Start timeframe</dt><dd>{safeSummaryValue(state.submitted.start_timeframe)}</dd></div>
        </dl>
        <p className="mt-5 text-stone-700">Browse <Link href={courseHint && courseHint in PATHWAY_COURSE_KEYS ? PATHWAY_COURSE_KEYS[courseHint as PathwayCourseKey] : "/training-course-calendar/"} className="font-semibold text-brand-700 underline">relevant training options and course dates</Link>.</p>
      </section>
    );
  }

  return (
    <section id={formId} className="scroll-mt-24 rounded-[2rem] border border-stone-200 bg-white p-6 shadow-sm sm:p-8">
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-600">Personal training review</p>
      <h2 className="mt-3 text-2xl font-bold text-stone-900 sm:text-3xl">{heading}</h2>
      <p className="mt-3 text-stone-600">{description}</p>
      {pathwayContext?.result?.optionIds?.length ? (
        <p className="mt-4 rounded-2xl bg-stone-50 px-4 py-3 text-sm text-stone-700">Your suggested options: {pathwayContext.result.optionIds.join(", ")}. Admissions will confirm any entry requirements.</p>
      ) : null}

      <noscript>
        <div className="mt-6 rounded-2xl border border-stone-200 bg-stone-50 p-5 text-sm leading-6 text-stone-700">
          JavaScript is unavailable, so this form cannot be submitted. Email <a className="font-semibold underline" href="mailto:admissions@cosmetic.college">admissions@cosmetic.college</a> or call <a className="font-semibold underline" href="tel:+443330155117">0333 015 5117</a> to discuss individual training.
        </div>
      </noscript>
      <form className="mt-8 space-y-5" onSubmit={handleSubmit(onSubmit)} onFocus={markStarted} data-private data-clarity-mask data-heap-redact-text data-sentry-block>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor={`${formId}-name`} className="mb-2 block text-sm font-medium text-stone-800">Name</label>
            <input id={`${formId}-name`} autoComplete="name" aria-invalid={!!errors.full_name} aria-describedby={errors.full_name ? `${formId}-name-error` : undefined} className={inputClass(!!errors.full_name)} {...register("full_name", { required: "Name is required", maxLength: { value: 201, message: "Please use 200 characters or fewer" }, validate: validateContactName })} />
            <FieldError id={`${formId}-name-error`} message={errors.full_name?.message} />
          </div>
          <div>
            <label htmlFor={`${formId}-email`} className="mb-2 block text-sm font-medium text-stone-800">Email</label>
            <input id={`${formId}-email`} type="email" autoComplete="email" aria-invalid={!!errors.email} aria-describedby={errors.email ? `${formId}-email-error` : undefined} className={inputClass(!!errors.email)} {...register("email", { required: "Email is required", maxLength: { value: 254, message: "Enter a valid email address" }, pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address" } })} />
            <FieldError id={`${formId}-email-error`} message={errors.email?.message} />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor={`${formId}-contact`} className="mb-2 block text-sm font-medium text-stone-800">Preferred contact method</label>
            <select id={`${formId}-contact`} aria-invalid={!!errors.preferred_contact_method} aria-describedby={errors.preferred_contact_method ? `${formId}-contact-error` : undefined} className={inputClass(!!errors.preferred_contact_method)} {...register("preferred_contact_method", { required: "Choose a contact method" })}>
              <option value="email">Email</option><option value="phone">Phone</option><option value="whatsapp">WhatsApp</option>
            </select>
            <FieldError id={`${formId}-contact-error`} message={errors.preferred_contact_method?.message} />
          </div>
          <div>
            <label htmlFor={`${formId}-phone`} className="mb-2 block text-sm font-medium text-stone-800">Phone number {preferredContact !== "email" ? <span aria-hidden="true">*</span> : <span className="font-normal text-stone-500">(only needed for phone or WhatsApp contact)</span>}</label>
            <input id={`${formId}-phone`} type="tel" autoComplete="tel" aria-required={preferredContact !== "email"} aria-invalid={!!errors.phone_number} aria-describedby={errors.phone_number ? `${formId}-phone-error` : undefined} className={inputClass(!!errors.phone_number)} {...register("phone_number", { validate: (value) => validateContactPhone(value || "", preferredContact !== "email"), maxLength: { value: 40, message: "Enter a valid phone number" } })} />
            <FieldError id={`${formId}-phone-error`} message={errors.phone_number?.message} />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor={`${formId}-status`} className="mb-2 block text-sm font-medium text-stone-800">Current background</label>
            <select id={`${formId}-status`} aria-invalid={!!errors.learner_status} aria-describedby={errors.learner_status ? `${formId}-status-error` : undefined} className={inputClass(!!errors.learner_status)} {...register("learner_status", { required: "Please select your background" })}>
              <option value="">Select</option>{LEARNER_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select><FieldError id={`${formId}-status-error`} message={errors.learner_status?.message} />
          </div>
          <div>
            <label htmlFor={`${formId}-interest`} className="mb-2 block text-sm font-medium text-stone-800">Training goal</label>
            <select id={`${formId}-interest`} aria-invalid={!!errors.area_of_interest} aria-describedby={errors.area_of_interest ? `${formId}-interest-error` : undefined} className={inputClass(!!errors.area_of_interest)} {...register("area_of_interest", { required: "Please select a goal" })}>
              <option value="">Select</option>{INTEREST_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select><FieldError id={`${formId}-interest-error`} message={errors.area_of_interest?.message} />
          </div>
        </div>

        <div>
          <label htmlFor={`${formId}-quals`} className="mb-2 block text-sm font-medium text-stone-800">Qualifications or experience summary <span className="font-normal text-stone-500">(optional)</span></label>
          <p className="mb-2 text-xs text-stone-500">Up to 500 characters. Please do not include patient, client or other clinical information.</p>
          <textarea id={`${formId}-quals`} rows={4} maxLength={500} aria-invalid={!!errors.current_qualifications} aria-describedby={errors.current_qualifications ? `${formId}-quals-error` : undefined} className={`${inputClass(!!errors.current_qualifications)} resize-y`} {...register("current_qualifications", { maxLength: { value: 500, message: "Please use 500 characters or fewer" } })} />
          <FieldError id={`${formId}-quals-error`} message={errors.current_qualifications?.message} />
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div><label htmlFor={`${formId}-route`} className="mb-2 block text-sm font-medium text-stone-800">Training type</label><select id={`${formId}-route`} className={inputClass(false)} {...register("training_route", { required: true })}>{TRAINING_ROUTE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
          <div><label htmlFor={`${formId}-start`} className="mb-2 block text-sm font-medium text-stone-800">When would you like to start? <span className="font-normal text-stone-500">(optional)</span></label><select id={`${formId}-start`} className={inputClass(false)} {...register("start_timeframe")}><option value="">Prefer not to say</option>{START_TIMEFRAME_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
          <div><label htmlFor={`${formId}-attendance`} className="mb-2 block text-sm font-medium text-stone-800">Practical attendance <span className="font-normal text-stone-500">(optional)</span></label><select id={`${formId}-attendance`} className={inputClass(false)} {...register("practical_attendance")}><option value="">Prefer not to say</option><option value="unsure">Unsure</option><option value="yes">I can attend practical training</option><option value="no">I may not be able to attend</option></select></div>
          <div><label htmlFor={`${formId}-trained`} className="mb-2 block text-sm font-medium text-stone-800">Have you trained with Cosmetic College before? <span className="font-normal text-stone-500">(optional)</span></label><select id={`${formId}-trained`} className={inputClass(false)} {...register("trained_with_cc_before")}><option value="">Prefer not to say</option>{TRAINED_WITH_CC_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
          <div><label htmlFor={`${formId}-booking`} className="mb-2 block text-sm font-medium text-stone-800">Who is this training for?</label><select id={`${formId}-booking`} className={inputClass(false)} {...register("booking_for", { required: true })}><option value="unsure">Not sure</option>{BOOKING_FOR_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
          <div><label htmlFor={`${formId}-finance`} className="mb-2 block text-sm font-medium text-stone-800">Would you like information about payment options? <span className="font-normal text-stone-500">(optional)</span></label><select id={`${formId}-finance`} className={inputClass(false)} {...register("payment_plan_interest")}><option value="">Prefer not to say</option>{PAYMENT_PLAN_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
        </div>

        {(currentBooking === "team" || currentBooking === "both") && <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4 text-sm text-stone-700">For staff or combined training, continue to the <Link href="/business-training/#planning-form" className="font-semibold text-brand-700 underline">team training request</Link>. Your personal details are not carried into that form.</div>}

        <label className="flex items-start gap-3 text-sm leading-6 text-stone-600"><input type="checkbox" className="mt-1 h-4 w-4 rounded border-stone-300 text-brand-600 focus:ring-brand-500" {...register("marketing_email_opt_in")} /><span>Email me Cosmetic College course news and offers. I can unsubscribe at any time.</span></label>
        <p className="text-xs leading-5 text-stone-500">We use your details to respond to this enquiry. See our <Link href="/privacy-policy/" className="underline">privacy policy</Link>.</p>
        {state.error && <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">{state.error}</p>}
        {currentBooking === "team" || currentBooking === "both" ? (
          <Link href="/business-training/#planning-form" className="inline-flex w-full items-center justify-center rounded-full bg-stone-900 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-brand-600 sm:w-auto">Continue to team training</Link>
        ) : (
          <button type="submit" disabled={!hydrated || state.loading} className="inline-flex w-full items-center justify-center rounded-full bg-stone-900 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">{state.loading ? "Submitting…" : submitButtonLabel}</button>
        )}
      </form>
    </section>
  );
}
